import { createApi } from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { handleAiErrorResponse } from '@/data/ai_errors';
import type {
  DeleteHistoryRecordRequest,
  DeleteHistoryRecordResponse,
  DownloadAnalysisPdfRequest,
  AiContentHistoryRow,
  SendContentAnalysisRequest,
  SendContentAnalysisResponseData,
  ViewHistoryMessageData,
  GetAiDetectionDetailsRequest,
  RunAiDetectionRequest,
  AiDetectionDetailsData,
  GetContentOverviewRequest,
  ContentOverviewData,
  RunSeoAnalysisRequest,
  GetEeatDetailsRequest,
  EeatDetailsData,
  GetAiSearchDetailsRequest,
  AiSearchDetailsData
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import type { ApiResponse } from '@/app/common/types/common';
import { getBaseApiPath } from '@/app/common/api/config';

type HistoryId = string | undefined;

const aiContentToolsApi = createApi({
  reducerPath: 'aiContentTools',
  tagTypes: [
    'AiContentHistory',
    'AiContentOverview',
    'AiDetectionDetails',
    'AiSeoDetails',
    'AiEeatDetails',
    'AiGeoDetails'
  ],
  baseQuery: (...args: unknown[]) =>
    customFetchBase(getBaseApiPath(), ...args),
  endpoints: build => ({
    sendContentAnalysis: build.mutation<
      ApiResponse<SendContentAnalysisResponseData>,
      SendContentAnalysisRequest
    >({
      query: (body: SendContentAnalysisRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis',
        method: 'POST',
        body
      }),
      invalidatesTags: ['AiContentHistory'],
      transformErrorResponse: handleAiErrorResponse
    }),
    getContentHistory: build.query<AiContentHistoryRow[], void>({
      query: () => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history',
        method: 'GET'
      }),
      providesTags: ['AiContentHistory'],
      keepUnusedDataFor: 3,
      transformResponse: (response: ApiResponse<AiContentHistoryRow[]>) => response.data,
      transformErrorResponse: handleAiErrorResponse
    }),
    viewHistoryMessage: build.query<ViewHistoryMessageData, HistoryId>({
      query: (id: HistoryId) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/details',
        method: 'POST',
        body: { id }
      }),
      transformResponse: (response: ApiResponse<ViewHistoryMessageData>) => response.data,
      providesTags: (_result, _error, id) =>
        id ? [{ type: 'AiSeoDetails', id }] : [],
      transformErrorResponse: handleAiErrorResponse
    }),
    getContentOverview: build.query<
      ApiResponse<ContentOverviewData | null>,
      GetContentOverviewRequest
    >({
      query: (body: GetContentOverviewRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/overview',
        method: 'POST',
        body
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AiContentOverview', id: arg.id }
      ],
      transformErrorResponse: handleAiErrorResponse
    }),
    deleteHistoryRecord: build.mutation<
      DeleteHistoryRecordResponse,
      DeleteHistoryRecordRequest
    >({
      query: (body: DeleteHistoryRecordRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/delete',
        method: 'DELETE',
        body
      }),
      invalidatesTags: ['AiContentHistory'],
      transformErrorResponse: handleAiErrorResponse
    }),
    downloadAnalysisPdf: build.mutation<Blob, DownloadAnalysisPdfRequest>({
      query: (body: DownloadAnalysisPdfRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/download-pdf',
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob()
      }),
      transformErrorResponse: handleAiErrorResponse
    }),
    getAiDetectionDetails: build.query<
      ApiResponse<AiDetectionDetailsData | null>,
      GetAiDetectionDetailsRequest
    >({
      query: (body: GetAiDetectionDetailsRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/ai-detection/details',
        method: 'POST',
        body,
        validateStatus: (response: Response) => response.status < 500
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AiDetectionDetails', id: arg.id }
      ],
      transformErrorResponse: handleAiErrorResponse
    }),
    runAiDetection: build.mutation<
      ApiResponse<AiDetectionDetailsData>,
      RunAiDetectionRequest
    >({
      query: (body: RunAiDetectionRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/ai-detection',
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiDetectionDetails', id: arg.id },
        { type: 'AiContentOverview', id: arg.id }
      ],
      transformErrorResponse: handleAiErrorResponse
    }),
    runSeoAnalysis: build.mutation<
      ApiResponse<ViewHistoryMessageData>,
      RunSeoAnalysisRequest
    >({
      query: (body: RunSeoAnalysisRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/seo-analysis',
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiSeoDetails', id: arg.id },
        'AiContentHistory'
      ],
      transformErrorResponse: handleAiErrorResponse
    }),
    getEeatDetails: build.query<
      ApiResponse<EeatDetailsData | null>,
      GetEeatDetailsRequest
    >({
      query: (body: GetEeatDetailsRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/eeat/details',
        method: 'POST',
        body,
        validateStatus: (response: Response) => response.status < 500
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AiEeatDetails', id: arg.id }
      ],
      transformErrorResponse: handleAiErrorResponse
    }),
    getAiSearchDetails: build.query<
      ApiResponse<AiSearchDetailsData | null>,
      GetAiSearchDetailsRequest
    >({
      query: (body: GetAiSearchDetailsRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/content-analysis/history/ai-search/details',
        method: 'POST',
        body,
        validateStatus: (response: Response) => response.status < 500
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AiGeoDetails', id: arg.id }
      ],
      transformErrorResponse: handleAiErrorResponse
    })
  })
});

export const {
  useSendContentAnalysisMutation,
  useGetContentHistoryQuery,
  useViewHistoryMessageQuery,
  useGetContentOverviewQuery,
  useDeleteHistoryRecordMutation,
  useDownloadAnalysisPdfMutation,
  useGetAiDetectionDetailsQuery,
  useRunAiDetectionMutation,
  useRunSeoAnalysisMutation,
  useGetEeatDetailsQuery,
  useGetAiSearchDetailsQuery
} = aiContentToolsApi;

export default aiContentToolsApi;
